import React, { useEffect, useState, useRef } from 'react';
import { sessionBundleAPI } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { SessionBundle } from '@shared/types';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useAuth0 } from '@auth0/auth0-react';
import ContentPageRenderer from '../components/ContentPageRenderer';
import { pushToDataLayer, clearEcommerce, formatSessionBundleItem } from '../utils/dataLayer';
import PromotionsTable from '../components/PromotionsTable';
import SEO from '../components/SEO';

type PromotionalItem = SessionBundle & {
  itemType: 'session_bundle' | 'subscription';
  pricingRules?: Array<{
    id: string;
    price: number;
    comparedPrice?: number | null;
    priority: number;
    startDate?: string;
    endDate?: string;
  }>;
};

const PromotionsPage: React.FC = () => {
  const { t, language } = useI18n();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useLocalizedNavigate();
  useDocumentTitle(t.pageTitles.promotions);

  const [promotionalItems, setPromotionalItems] = useState<PromotionalItem[]>([]);
  const [loading, setLoading] = useState(true);

  const hasTrackedViewItemList = useRef(false);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const response = await sessionBundleAPI.getAll();
        const apiData = (response.data as any)?.data;
        
        let allItems: PromotionalItem[] = [];
        
        if (apiData && apiData.sessionBundles && Array.isArray(apiData.sessionBundles)) {
          allItems = apiData.sessionBundles.map((sessionBundle: any) => ({
            ...sessionBundle,
            itemType: sessionBundle.type === 'subscription' ? 'subscription' : 'session_bundle',
          }));
        } else if (response.data && Array.isArray(response.data)) {
          allItems = response.data.map((sessionBundle: any) => ({
            ...sessionBundle,
            itemType: sessionBundle.type === 'subscription' ? 'subscription' : 'session_bundle',
          }));
        }
        
        const promoItems = allItems.reduce<PromotionalItem[]>((acc, item) => {
          if (item.pricingRules && item.pricingRules.length > 0) {
            const now = new Date();
            const activeRule = item.pricingRules.find(rule => {
              const startValid = !rule.startDate || new Date(rule.startDate) <= now;
              const endValid = !rule.endDate || new Date(rule.endDate) >= now;
              return startValid && endValid && rule.priority >= 100;
            });

            if (activeRule) {
              acc.push({
                ...item,
                price: activeRule.price / 100,
                originalPrice: activeRule.comparedPrice ? activeRule.comparedPrice / 100 : item.originalPrice
              });
            }
          }
          return acc;
        }, []);

        promoItems.sort((a, b) => {
          const aPriority = Math.max(...(a.pricingRules?.map(r => r.priority) || [0]));
          const bPriority = Math.max(...(b.pricingRules?.map(r => r.priority) || [0]));
          if (aPriority !== bPriority) {
            return bPriority - aPriority;
          }
          return (a.price ?? 0) - (b.price ?? 0);
        });

        setPromotionalItems(promoItems);
      } catch (error) {
        console.error('Failed to fetch promotional items:', error);
      }
      setLoading(false);
    };

    fetchItems();
  }, []);

  useEffect(() => {
    if (promotionalItems.length > 0 && !loading && !hasTrackedViewItemList.current) {
      hasTrackedViewItemList.current = true;
      clearEcommerce();
      pushToDataLayer('view_item_list', {
        content: { category: 'promotion' },
        ecommerce: {
          items: promotionalItems.map((item) => formatSessionBundleItem(item, 'Promotion')),
        },
      });
    }
  }, [promotionalItems, loading]);

  const handlePurchase = (sessionBundleId: string) => {
    const item = promotionalItems.find((p) => p.id === sessionBundleId);
    const itemTypeSegment = item?.type === 'subscription' ? 'subscription' : 'session-bundle';
    
    if (item) {
      clearEcommerce();
      pushToDataLayer('add_to_cart', {
        content: { category: 'promotion' },
        ecommerce: {
          items: [formatSessionBundleItem(item, 'Promotion')],
        },
      });
    }

    if (!isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo: `/checkout/${itemTypeSegment}/${sessionBundleId}`,
        },
      });
    } else {
      navigate(`/checkout/${itemTypeSegment}/${sessionBundleId}`);
    }
  };

  if (loading) {
    return (
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {t.sessionBundles.loadingSessionBundles}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 bg-gray-50">
      <SEO description={t.seo.promotionsDescription} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t.promotions}</h1>
        </div>

        {promotionalItems.length > 0 && (
          <ContentPageRenderer
            slug="promo-head"
            className="max-w-3xl mx-auto mb-12"
          />
        )}

        {promotionalItems.length > 0 ? (
          <>
            <div className="max-w-5xl mx-auto">
              <PromotionsTable
                items={promotionalItems}
                language={language}
                onPurchase={handlePurchase}
              />
            </div>
            <ContentPageRenderer
              slug="promo-foot"
              className="max-w-3xl mx-auto mt-12"
            />
          </>
        ) : (
          <ContentPageRenderer
            slug="promo-inactive"
            className="max-w-3xl mx-auto"
          />
        )}
      </div>
    </div>
  );
};

export default PromotionsPage;
